"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useState, useEffect } from "react";

import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Typography } from "../../components/ui/Typography";
import { useAuthContext } from "../../contexts/AuthContext";
import { LoginUser, UserRole } from "../../types";

export default function LoginPage() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const { login, isLoading: authIsLoading } = useAuthContext();

	// Get redirect URL from query parameters
	const redirectUrl = searchParams.get("redirect");

	// Local loading state to prevent hydration mismatch
	const [isLoading, setIsLoading] = useState(false);

	// Sync with auth loading state after hydration
	useEffect(() => {
		setIsLoading(authIsLoading);
	}, [authIsLoading]);

	const [formData, setFormData] = useState<LoginUser>({
		email: "",
		password: "",
	});

	const handleInputChange = (field: string, value: string) => {
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			setIsLoading(true);
			const { role } = await login(formData);
			// Redirect to the original URL or default to /app
			router.push(redirectUrl || role == UserRole.ADMIN ? "/app/users" : "/app/dashboard");
		} catch (error) {
			// Error is already handled by the useAuth hook with toast
			console.error("Login error:", error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Layout>
			<div className="flex flex-col items-center gap-20 py-16">
				{/* Header Section */}
				<div className="flex flex-col gap-8 items-center text-center w-[562px]">
					<Typography variant="h2" color="blu-medio">
						Benvenuto!
					</Typography>
					<Typography variant="txt-18" color="blu-scuro">
						Accedi in pochi secondi e riprendi da dove avevi lasciato.
					</Typography>
				</div>

				{/* Form Section */}
				<div className="w-[392px]">
					<form onSubmit={handleSubmit} className="flex flex-col gap-10">
						<div className="flex flex-col gap-6">
							{/* Email Input */}
							<div className="flex flex-col gap-4">
								<Typography variant="txt-16" color="blu-scuro">
									Indirizzo email
								</Typography>
								<Input
									type="email"
									placeholder="Indirizzo email"
									value={formData.email}
									onValueChange={(value) => handleInputChange("email", value)}
									required
									fullWidth
								/>
							</div>

							{/* Password Input */}
							<div className="flex flex-col gap-4">
								<Typography variant="txt-16" color="blu-scuro">
									Password
								</Typography>
								<Input
									type="password"
									placeholder="Password"
									value={formData.password}
									onValueChange={(value) => handleInputChange("password", value)}
									required
									fullWidth
								/>
							</div>

							{/* Forgot Password Link */}
							<Link href="/forgot-password">
								<Typography
									variant="txt-16"
									color="blu-medio"
									className="hover:text-blu-chiaro transition-colors cursor-pointer"
								>
									Password dimenticata?
								</Typography>
							</Link>
						</div>



						{/* Submit Button */}
						<Button
							type="submit"
							variant="primary"
							buttonSize="lg"
							fullWidth
							state={isLoading ? "loading" : "default"}
							loadingText="Accesso in corso..."
						>
							Accedi
						</Button>
					</form>

					{/* Sign Up Link */}
					<div className="mt-6 text-center">
						<Typography variant="txt-16" color="blu-scuro">
							Non hai un account?{" "}
							<Link href="/sign-up">
								<span className="text-blu-medio hover:text-blu-chiaro transition-colors cursor-pointer">
									Registrati
								</span>
							</Link>
						</Typography>
					</div>
				</div>
			</div>
		</Layout>
	);
}
